import { AppstoreAddOutlined } from '@ant-design/icons';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type {
  IHotelWithDetails,
  IMergeExperienceItem,
  IMergePackageItem,
  IMergeRoomItem,
} from 'app/typings/migrationTool/HotelWithDetails';

import { EmptyState } from '../../Extranet/commons/EmptyState/EmptyState';
import type { MergeSelection } from '../HotelMigration';
import type { SelectActionEnum } from '../_components/HotelServiceCard/HotelServiceCard';
import type { ActionType } from '../_components/MergeAction/MergeAction';
import { MergeAction } from '../_components/MergeAction/MergeAction';
import { getSelectedParentItem } from '../utils';

import './MigrationLayout.scss';

export type MergeItem =
  | (IMergeRoomItem & { bedsList?: string[] })
  | IMergeExperienceItem
  | IMergePackageItem;

type MigrationLayoutProps<T extends MergeItem> = {
  title: string;
  description: string;
  childHotel: string;
  hotel: IHotelWithDetails;
  actionList: ActionType[];
  items: T[];
  getItemId: (item: T) => number;
  entityLabel: 'Room' | 'Experience' | 'Package';
  selections: Record<number, MergeSelection>;
  setSelections: (newSelections: Record<number, MergeSelection>) => void;
};

export const MigrationLayout = <T extends MergeItem>({
  title,
  description,
  childHotel,
  hotel,
  actionList,
  items,
  getItemId,
  entityLabel,
  selections,
  setSelections,
}: MigrationLayoutProps<T>) => {
  const { data: bedCategories } = useBedCategories();
  const { currencyFormatter } = useContext(CurrencyContext);

  if (!items.length) {
    return (
      <EmptyState
        icon={<AppstoreAddOutlined />}
        title={`No ${entityLabel.toLowerCase()}s found to merge`}
      />
    );
  }

  return (
    <div className="migration-layout">
      <div className="migration-header">
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <div className="migration-content">
        <div className="merge-actions">
          {items.map((item, idx) => {
            const itemId = getItemId(item);
            const selectedValue = selections[itemId];

            return (
              <MergeAction<T>
                key={itemId}
                childService={item}
                parentService={hotel}
                actionList={actionList}
                selectAction={selectedValue?.action}
                selectTargetId={selectedValue?.targetId}
                selectedParentItem={getSelectedParentItem(
                  selectedValue,
                  itemId,
                  items,
                  entityLabel,
                  hotel,
                  currencyFormatter,
                  bedCategories
                )}
                onSelectAction={(action: SelectActionEnum, targetId?: number) =>
                  setSelections({
                    ...selections,
                    [itemId]: {
                      action,
                      ...(targetId ? { targetId } : {}),
                    },
                  })
                }
                entityLabel={entityLabel}
                isFirstActionOfList={idx === 0}
                childHotel={childHotel}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
